import { useState, useMemo } from "react";
import { format, subMonths, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { es } from "date-fns/locale";
import {
  CalendarIcon, FileText, DollarSign, Clock, TrendingUp, Users, Trash2, Eye,
  Weight, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus
} from "lucide-react";
import { useAllDocuments, COP_RATE, DocumentRecord } from "@/hooks/useDocuments";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from "recharts";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { TableToolbar, exportToCSV } from "@/components/TableToolbar";
import { ResizableTableHead } from "@/components/ResizableTableHead";
import { TruncatedCell } from "@/components/TruncatedCell";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DocumentViewerModal } from "@/components/DocumentViewerModal";

/* ── Helpers ─────────────────────────────────────────── */

function safeNum(v: any): number {
  return parseFloat(String(v)) || 0;
}

/** Compute month-over-month % change */
function computeMoM(docs: DocumentRecord[], field: (d: DocumentRecord) => number) {
  const now = new Date();
  const curStart = startOfMonth(now);
  const curEnd = endOfMonth(now);
  const prevStart = startOfMonth(subMonths(now, 1));
  const prevEnd = endOfMonth(subMonths(now, 1));

  let curVal = 0, prevVal = 0;
  for (const d of docs) {
    const date = new Date(d.created_at);
    if (isWithinInterval(date, { start: curStart, end: curEnd })) curVal += field(d);
    if (isWithinInterval(date, { start: prevStart, end: prevEnd })) prevVal += field(d);
  }
  if (prevVal === 0) return curVal > 0 ? 100 : 0;
  return ((curVal - prevVal) / prevVal) * 100;
}

/** Count documents that needed manual adjustment (descuento_ajuste != 0 or estado changed) */
function countAdjusted(docs: DocumentRecord[]): number {
  return docs.filter(d => safeNum(d.descuento_ajuste) !== 0).length;
}

/* ── Main Component ──────────────────────────────────── */

export default function ExecutiveSummary() {
  const { data: allDocuments = [] } = useAllDocuments();
  const [dateFrom, setDateFrom] = useState<Date | undefined>(new Date("2026-01-01"));
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date("2026-12-31"));

  const allUniqueTypes = useMemo(() => {
    const types = new Set(allDocuments.map(d => d.tipo_documento).filter(Boolean));
    return [...types].sort();
  }, [allDocuments]);

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const activeTypes = selectedTypes.length > 0 ? selectedTypes : allUniqueTypes;

  const toggleType = (t: string) => {
    setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const filtered = useMemo(() => {
    return allDocuments.filter(d => {
      const date = new Date(d.created_at);
      const fromDate = dateFrom ? new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()) : undefined;
      const toDate = dateTo ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59) : undefined;
      const inRange = (!fromDate || date >= fromDate) && (!toDate || date <= toDate);
      const inType = activeTypes.includes(d.tipo_documento as string);
      return inRange && inType;
    });
  }, [allDocuments, dateFrom, dateTo, activeTypes]);

  /* ── KPI calculations ──────────────────────────────── */
  const totalUSD = filtered.filter(d => (d.moneda || "").toUpperCase() === "USD").reduce((s, d) => s + safeNum(d.total_factura), 0);
  const totalCOP = filtered.filter(d => (d.moneda || "").toUpperCase() === "COP").reduce((s, d) => s + safeNum(d.total_factura), 0);
  const totalWeight = filtered.reduce((s, d) => s + safeNum(d.peso_bruto_kg), 0);
  const hoursEstimate = filtered.length * 2.5;

  // New KPI: Costo Promedio por KG
  const totalInvoiceValue = filtered.reduce((s, d) => s + safeNum(d.total_factura), 0);
  const costPerKg = totalWeight > 0 ? totalInvoiceValue / totalWeight : 0;

  // New KPI: Tasa de Error de Extracción
  const adjustedCount = countAdjusted(filtered);
  const errorRate = filtered.length > 0 ? (adjustedCount / filtered.length) * 100 : 0;

  // Month-over-month variations
  const momUSD = computeMoM(
    filtered.filter(d => (d.moneda || "").toUpperCase() === "USD"),
    d => safeNum(d.total_factura)
  );
  const momCOP = computeMoM(
    filtered.filter(d => (d.moneda || "").toUpperCase() === "COP"),
    d => safeNum(d.total_factura)
  );
  const momWeight = computeMoM(filtered, d => safeNum(d.peso_bruto_kg));
  const momDocs = computeMoM(filtered, () => 1);

  /* ── Trend data (monthly) ──────────────────────────── */
  const trendData = useMemo(() => {
    const monthMap = new Map<string, { month: string; gasto: number; peso: number }>();
    filtered.forEach(d => {
      const date = new Date(d.created_at);
      const key = format(date, "yyyy-MM");
      const label = format(date, "MMM yy", { locale: es });
      if (!monthMap.has(key)) monthMap.set(key, { month: label, gasto: 0, peso: 0 });
      const entry = monthMap.get(key)!;
      entry.gasto += safeNum(d.total_factura);
      entry.peso += safeNum(d.peso_bruto_kg);
    });
    return [...monthMap.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  }, [filtered]);

  /* ── Top 5 Proveedores con discrepancias ───────────── */
  const discrepancyProviders = useMemo(() => {
    const map = new Map<string, number>();
    filtered.forEach(d => {
      if (safeNum(d.descuento_ajuste) !== 0) {
        const name = d.emisor || "N/A";
        map.set(name, (map.get(name) || 0) + 1);
      }
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [filtered]);

  /* ── Top 5 emisores ────────────────────────────────── */
  const topEmitters = useMemo(() => {
    const map = new Map<string, number>();
    filtered.forEach(d => map.set(d.emisor || "N/A", (map.get(d.emisor || "N/A") || 0) + 1));
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [filtered]);

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      {/* Filters */}
      <div className="glass rounded-xl p-4 flex flex-wrap items-center gap-3 animate-fade-in">
        <DateFilter label="Desde" date={dateFrom} onSelect={setDateFrom} />
        <DateFilter label="Hasta" date={dateTo} onSelect={setDateTo} />
        <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
        <div className="flex flex-wrap gap-2">
          {allUniqueTypes.map(t => (
            <Button
              key={t}
              size="sm"
              variant={activeTypes.includes(t) && selectedTypes.length > 0 ? "default" : selectedTypes.length === 0 ? "default" : "outline"}
              onClick={() => toggleType(t)}
              className={cn(
                "text-xs h-8",
                (activeTypes.includes(t) && selectedTypes.length > 0) || selectedTypes.length === 0
                  ? "bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-primary hover:border-primary/50"
              )}
            >
              {t}
            </Button>
          ))}
        </div>
      </div>

      {/* KPIs Row 1: Main financial */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in" style={{ animationDelay: "0.05s" }}>
        <KPICard icon={FileText} label="Documentos Procesados" value={String(filtered.length)} mom={momDocs} />
        <KPICard icon={DollarSign} label="Total USD" value={`$${totalUSD.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} mom={momUSD} />
        <KPICard icon={DollarSign} label="Total COP" value={`$${totalCOP.toLocaleString("es-CO", { minimumFractionDigits: 2 })}`} mom={momCOP} iconClassName="bg-secondary/10" iconColor="text-secondary" />
      </div>

      {/* KPIs Row 2: Efficiency */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in" style={{ animationDelay: "0.08s" }}>
        <KPICard icon={Weight} label="Peso Total (KG)" value={totalWeight.toLocaleString("en-US", { minimumFractionDigits: 2 })} mom={momWeight} />
        <KPICard
          icon={TrendingUp}
          label="Costo Promedio / KG"
          value={`$${costPerKg.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub="Total facturado ÷ peso bruto"
          iconClassName="bg-accent/10"
          iconColor="text-accent"
        />
        <KPICard
          icon={AlertTriangle}
          label="Tasa de Error Extracción"
          value={`${errorRate.toFixed(1)}%`}
          sub={`${adjustedCount} de ${filtered.length} con ajustes`}
          iconClassName={errorRate > 20 ? "bg-destructive/10" : "bg-primary/10"}
          iconColor={errorRate > 20 ? "text-destructive" : "text-primary"}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
        {/* Dual-axis Trend Line */}
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Tendencia: Gasto vs Peso por Mes</h3>
          </div>
          <div className="h-72">
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={{ stroke: "hsl(var(--border))" }} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: "hsl(var(--primary))", fontSize: 11 }}
                    axisLine={{ stroke: "hsl(var(--primary))" }}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    label={{ value: "Gasto ($)", angle: -90, position: "insideLeft", fill: "hsl(var(--primary))", fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: "hsl(var(--secondary))", fontSize: 11 }}
                    axisLine={{ stroke: "hsl(var(--secondary))" }}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                    label={{ value: "Peso (KG)", angle: 90, position: "insideRight", fill: "hsl(var(--secondary))", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      color: "hsl(var(--foreground))",
                      fontSize: 12,
                    }}
                    formatter={(value: number, name: string) => {
                      if (name === "gasto") return [`$${value.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, "Gasto Total"];
                      return [`${value.toLocaleString("en-US", { minimumFractionDigits: 2 })} KG`, "Peso Bruto"];
                    }}
                  />
                  <Line yAxisId="left" type="monotone" dataKey="gasto" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ fill: "hsl(var(--primary))", r: 4 }} activeDot={{ r: 6 }} />
                  <Line yAxisId="right" type="monotone" dataKey="peso" stroke="hsl(var(--secondary))" strokeWidth={2.5} dot={{ fill: "hsl(var(--secondary))", r: 4 }} activeDot={{ r: 6 }} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-sm text-muted-foreground">Sin datos para los filtros seleccionados</p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-6 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-primary rounded" />
              <span className="text-xs text-muted-foreground">Gasto ($)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-secondary rounded border-dashed" style={{ borderTop: "2px dashed hsl(var(--secondary))", height: 0 }} />
              <span className="text-xs text-muted-foreground">Peso (KG)</span>
            </div>
          </div>
        </div>

        {/* Top 5 Emisores */}
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Top 5 Emisores</h3>
          </div>
          <div className="space-y-3">
            {topEmitters.map(([name, count], i) => (
              <div key={name} className="flex items-center gap-3">
                <span className="text-xs font-bold text-primary w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{name}</p>
                  <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(count / (topEmitters[0]?.[1] || 1)) * 100}%`,
                        background: i === 0 ? "hsl(var(--primary))" : "hsl(var(--secondary))",
                      }}
                    />
                  </div>
                </div>
                <span className="text-xs font-mono text-muted-foreground">{count} docs</span>
              </div>
            ))}
            {topEmitters.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">Sin datos</p>
            )}
          </div>
        </div>
      </div>

      {/* Discrepancy analysis */}
      <div className="glass rounded-xl p-5 animate-fade-in" style={{ animationDelay: "0.12s" }}>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <h3 className="text-sm font-semibold text-foreground">Top 5 Proveedores con Discrepancias</h3>
          <span className="text-xs text-muted-foreground ml-auto">Documentos con ajustes manuales (descuento/ajuste ≠ 0)</span>
        </div>
        {discrepancyProviders.length > 0 ? (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={discrepancyProviders.map(([name, count]) => ({ name, count }))} layout="vertical" barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={{ stroke: "hsl(var(--border))" }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: "hsl(var(--foreground))", fontSize: 11 }} width={140} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    color: "hsl(var(--foreground))",
                    fontSize: 12,
                  }}
                  formatter={(v: number) => [`${v} discrepancias`, "Total"]}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {discrepancyProviders.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "hsl(var(--destructive))" : `hsl(var(--destructive) / ${0.7 - i * 0.1})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">🎉 Sin discrepancias detectadas en los documentos filtrados</p>
          </div>
        )}
      </div>

      {/* Global Data Table */}
      <GlobalDocumentsTable documents={filtered} />
    </main>
  );
}

/* ── KPI Card with MoM indicator ─────────────────────── */

function MoMBadge({ value }: { value?: number }) {
  if (value === undefined || value === null) return null;
  const abs = Math.abs(value);
  const isUp = value > 0;
  const isZero = abs < 0.1;

  if (isZero) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
        <Minus className="h-3 w-3" />
        <span>0% vs mes ant.</span>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex items-center gap-1 text-[10px] font-medium",
      isUp ? "text-primary" : "text-destructive"
    )}>
      {isUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      <span>{isUp ? "+" : ""}{abs.toFixed(1)}% vs mes ant.</span>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, mom, iconClassName, iconColor }: {
  icon: any; label: string; value: string; sub?: string; mom?: number;
  iconClassName?: string; iconColor?: string;
}) {
  return (
    <div className="glass rounded-xl p-5 glow-cyan transition-all hover:glow-cyan-strong">
      <div className="flex items-center gap-3 mb-2">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", iconClassName || "bg-primary/10")}>
          <Icon className={cn("h-4 w-4", iconColor || "text-primary")} />
        </div>
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
      </div>
      <p className="text-3xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      {mom !== undefined && <div className="mt-1.5"><MoMBadge value={mom} /></div>}
    </div>
  );
}

/* ── Supporting components (unchanged) ───────────────── */

function formatUploadDate(dateStr: string): string {
  try { return format(new Date(dateStr), "dd/MM/yyyy HH:mm"); } catch { return dateStr; }
}

function GlobalDocumentsTable({ documents }: { documents: DocumentRecord[] }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("__all__");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const uniqueTypes = useMemo(() => {
    const types = new Set(documents.map(d => d.tipo_documento).filter(Boolean));
    return [...types].sort();
  }, [documents]);

  const filtered = useMemo(() => {
    return documents.filter(d => {
      const matchesSearch =
        d.no_documento.toLowerCase().includes(search.toLowerCase()) ||
        (d.emisor || "").toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === "__all__" || d.tipo_documento === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [documents, search, typeFilter]);

  const handleExport = () => {
    const headers = ["Fecha Subida", "Fecha", "Tipo", "No Documento", "Emisor", "Receptor", "Moneda", "Total", "Origen", "Destino", "Notas"];
    const rows = filtered.map(d => [formatUploadDate(d.created_at), d.fecha, d.tipo_documento, d.no_documento, d.emisor, d.receptor, d.moneda, String(d.total_factura), d.origen, d.destino, d.notas_tecnicas]);
    exportToCSV(headers, rows, "resumen_documentos.csv");
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.rpc('soft_delete_document', { p_doc_id: deleteId });
    if (error) { toast({ title: "Error al archivar", description: error.message, variant: "destructive" }); return; }
    toast({ title: "📦 Documento archivado" });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["document-types"] });
    setDeleteId(null);
  };

  const thClass = "text-xs text-muted-foreground font-semibold";

  return (
    <div className="rounded-xl overflow-hidden bg-card border border-border shadow-sm animate-fade-in" style={{ animationDelay: "0.15s" }}>
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Todos los Documentos</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{filtered.length} registros</p>
      </div>
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <div className="relative flex-1 max-w-sm">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por No. Documento o Emisor..."
            className="w-full pl-3 pr-3 py-2 bg-muted/30 border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px] border-border text-sm">
            <SelectValue placeholder="Tipo de Documento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los tipos</SelectItem>
            {uniqueTypes.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleExport} className="gap-2 border-border text-muted-foreground hover:text-primary hover:border-primary/50">
          <FileText className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>
      <ScrollArea className="w-full">
        <div className="min-w-[1200px]">
          <Table style={{ tableLayout: "fixed" }}>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <ResizableTableHead className={thClass} initialWidth={130} minWidth={90}>Fecha Subida</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>Fecha</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={100} minWidth={60}>Tipo</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={110} minWidth={70}>No Documento</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={130} minWidth={80}>Emisor</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={130} minWidth={80}>Receptor</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={70} minWidth={50}>Moneda</ResizableTableHead>
                <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>Total</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>Origen</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>Destino</ResizableTableHead>
                <ResizableTableHead className={thClass} initialWidth={120} minWidth={70}>Notas</ResizableTableHead>
                <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={80} minWidth={60}>Auditoría</ResizableTableHead>
                <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={100} minWidth={70}>Acciones</ResizableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((doc) => (
                <TableRow key={doc.id} className="border-border hover:bg-muted/30 transition-colors">
                  <TableCell className="text-xs text-muted-foreground max-w-[130px]"><TruncatedCell>{formatUploadDate(doc.created_at)}</TruncatedCell></TableCell>
                  <TableCell className="text-xs max-w-[90px]"><TruncatedCell>{doc.fecha}</TruncatedCell></TableCell>
                  <TableCell className="text-xs max-w-[100px]">
                    <TruncatedCell>
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                        {doc.tipo_documento}
                      </span>
                    </TruncatedCell>
                  </TableCell>
                  <TableCell className="text-xs font-mono max-w-[110px]"><TruncatedCell>{doc.no_documento}</TruncatedCell></TableCell>
                  <TableCell className="text-xs max-w-[130px]"><TruncatedCell>{doc.emisor}</TruncatedCell></TableCell>
                  <TableCell className="text-xs max-w-[130px]"><TruncatedCell>{doc.receptor}</TruncatedCell></TableCell>
                  <TableCell className="text-xs">{doc.moneda}</TableCell>
                  <TableCell className="text-xs text-right font-mono">
                    {Number(doc.total_factura) > 0 ? `$${Number(doc.total_factura).toLocaleString()}` : "—"}
                  </TableCell>
                  <TableCell className="text-xs max-w-[90px]"><TruncatedCell>{doc.origen}</TruncatedCell></TableCell>
                  <TableCell className="text-xs max-w-[90px]"><TruncatedCell>{doc.destino}</TruncatedCell></TableCell>
                  <TableCell className="text-xs max-w-[120px] text-muted-foreground"><TruncatedCell>{doc.notas_tecnicas}</TruncatedCell></TableCell>
                  <TableCell className="text-center">
                    {doc.archivo_url ? (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setViewerUrl(doc.archivo_url!); setViewerTitle(doc.no_documento); }}>
                        <Eye className="h-4 w-4 text-primary" />
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteId(doc.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <DocumentViewerModal
        open={!!viewerUrl}
        onOpenChange={(open) => { if (!open) setViewerUrl(null); }}
        url={viewerUrl || ""}
        title={viewerTitle}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent className="glass border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar documento?</AlertDialogTitle>
            <AlertDialogDescription>El documento y sus ítems de inventario se ocultarán de la vista principal. Podrá ser recuperado por un administrador si es necesario.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-amber-600 text-white hover:bg-amber-700">Archivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DateFilter({ label, date, onSelect }: { label: string; date?: Date; onSelect: (d?: Date) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("gap-2 text-xs border-border", !date && "text-muted-foreground")}>
          <CalendarIcon className="h-3.5 w-3.5" />
          {date ? format(date, "dd MMM yyyy", { locale: es }) : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={onSelect} initialFocus className="p-3 pointer-events-auto" />
      </PopoverContent>
    </Popover>
  );
}
